import {useCallback, useEffect, useRef, useState} from 'react';
import {useBannerViewGtag} from 'hooks/analytics/gtag/banners/useBannerViewGtag';

interface IViewedItem {
    id: string;
    index: number;
}

interface IBannerItem {
    id: string;
    name: string;
    position: number;
}

export interface IUseSliderBannerViewTrackingParams<T> {
    list: T[];
    isLoading?: boolean;
    getId: (item: T) => string;
    getName: (item: T) => string;
    type: 'common' | 'stock';
    creative: 'main_slider' | 'promo_page_main' | 'promo_page_another_promo' | 'promo_page_trust' | 'snippet__banner';
    enabled?: boolean;
}

export const useSliderBannerViewTracking = <T>({
    list,
    isLoading,
    getId,
    getName,
    type,
    creative,
    enabled = true,
}: IUseSliderBannerViewTrackingParams<T>) => {
    const bannerViewGtag = useBannerViewGtag();
    const [viewedItems, setViewedItems] = useState<IViewedItem[]>([]);
    const [sentItems, setSentItems] = useState<Set<string>>(new Set());

    const listRef = useRef(list);
    listRef.current = list;

    const handleItemView = useCallback(
        (id: string, index: number) => {
            if (!enabled) {
                return;
            }

            setViewedItems((prevItems) =>
                prevItems.some((item) => item.id === id) ? prevItems : [...prevItems, {id, index}],
            );
        },
        [enabled],
    );

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const newItems = viewedItems.filter((item) => !sentItems.has(item.id));

        if (0 < newItems.length) {
            const newItemsMap = new Map(newItems.map((item) => [item.id, item.index]));

            const bannersToSend: IBannerItem[] = listRef.current
                .filter((item) => newItemsMap.has(getId(item)))
                .map((item) => ({
                    id: getId(item),
                    name: getName(item),
                    position: newItemsMap.get(getId(item))!,
                }));

            if (0 < bannersToSend.length) {
                bannersToSend.forEach((banner) => {
                    bannerViewGtag({
                        type,
                        creative,
                        banners: [banner],
                        enableTracking: false,
                    });
                });

                setSentItems((prev) => {
                    const newSent = new Set(prev);
                    bannersToSend.forEach((banner) => {
                        newSent.add(banner.id);
                    });
                    return newSent;
                });
            }
        }
    }, [viewedItems, sentItems, bannerViewGtag, isLoading, getId, getName, type, creative]);

    return {handleItemView};
};
